import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"

import * as Yup from "yup"
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, useMediaQuery, useTheme } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Button, InputText, NotificationUtils, Select, TextArea, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormik } from "formik"

import { KonanAPI } from "../../services/KonanAPI"
import type { CreateIssueRequest } from "../../types/custom/core"

type Props = {
  open: boolean
  onClose: () => void
}

export function SupportDialog(props: Readonly<Props>): React.ReactElement {
  const { open, onClose } = props
  const { t } = useTranslation("dialogs")
  const theme = useTheme()

  const [issueType, setIssueType] = useState<string>("Issue")

  const validationSchema = Yup.object({
    summary: Yup.string()
      .required(t("SupportDialog.validation.summaryRequired"))
      .min(1, t("SupportDialog.validation.summaryEmpty"))
      .max(500, t("SupportDialog.validation.summaryTooLong")),
    description: Yup.string()
      .max(500, t("SupportDialog.validation.descriptionTooLong"))
      .required(t("SupportDialog.validation.descriptionRequired")),
  })

  const createIssueMutation = useMutation<AxiosResponse<string>, AxiosError, CreateIssueRequest>(KonanAPI.createIssue, {
    onSuccess: () =>
      NotificationUtils.toast(t("SupportDialog.messages.ticketCreated"), {
        severity: "success",
      }),
    onError: () =>
      NotificationUtils.toast(t("SupportDialog.messages.feedbackError"), {
        severity: "error",
      }),
  })

  const handleChange = (e: InputChangeEvent): void => {
    setIssueType(e.target.value as string)
  }

  const formik = useFormik({
    initialValues: {
      summary: "",
      description: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      await createIssueMutation
        .mutateAsync({
          summary: values.summary,
          issueType:
            issueType === t("SupportDialog.issueTypes.issue")
              ? t("SupportDialog.issueTypes.bug")
              : t("SupportDialog.issueTypes.task"),
          description: values.description,
        })
        .then(() => {
          onClose()
          resetForm({})
        })
    },
  })

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if ((reason === "backdropClick" || reason === "escapeKeyDown") && createIssueMutation.isLoading) {
          return undefined
        } else {
          onClose()
        }
      }}
      fullWidth
      fullScreen={useMediaQuery(theme.breakpoints.down("md"))}
    >
      <DialogTitle id="form-dialog-title" className="dialog-header-base">
        <Typography variant="h2-bold">{t("SupportDialog.title")}</Typography>
      </DialogTitle>

      <DialogContent className="dialog-content-base">
        <Select
          label={t("SupportDialog.labels.openTicket")}
          value={issueType}
          handleChange={handleChange}
          options={[t("SupportDialog.options.issue"), t("SupportDialog.options.suggestIdea")]}
        />

        <Box pt={1} />

        <InputText
          id="summary"
          label={t("SupportDialog.labels.summary")}
          required
          fullWidth
          hideDescription
          value={formik.values.summary}
          handleChange={formik.handleChange}
          handleBlur={formik.handleBlur}
          error={formik.touched.summary && Boolean(formik.errors.summary) && formik.errors.summary}
          placeholder={t("SupportDialog.placeholders.summary")}
        />

        <Grid item xs={12} marginTop={1}>
          <TextArea
            id="description"
            name="description"
            label={t("SupportDialog.labels.description")}
            required
            // Fix fullWidth in SUI
            // fullWidth
            width={552}
            multiple
            value={formik.values.description}
            handleChange={formik.handleChange}
            heightControl={{ lines: 4 }}
            placeholder={
              issueType === t("SupportDialog.issueTypes.suggest")
                ? t("SupportDialog.placeholders.suggestIdea")
                : t("SupportDialog.placeholders.issue")
            }
            hideDescription
          />
        </Grid>
      </DialogContent>

      <DialogActions className="dialog-actions-base">
        <Button onClick={() => onClose()} variant={"secondary"} disabled={createIssueMutation.isLoading}>
          {t("SupportDialog.buttons.cancel")}
        </Button>
        <Button
          onClick={formik.submitForm}
          variant={"primary"}
          disabled={formik.isSubmitting || !(formik.dirty && formik.isValid)}
          isLoading={createIssueMutation.isLoading}
        >
          {t("SupportDialog.buttons.send")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
